// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Tab.tsx
 * @input Uses React, StyleX, TabListContext
 * @output Exports Tab component and TabProps type
 * @position Core tab item; renders as button or anchor in navigation with a
 *   divider-overlay selected indicator
 *
 * SYNC: When modified, update:
 * - /packages/core/src/TabList/TabList.doc.mjs
 * - /packages/core/src/TabList/index.ts
 * - /packages/core/src/TabList/TabList.test.tsx
 * - /packages/cli/templates/blocks/components/TabList/ (showcase blocks)
 */
import React, { useCallback } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, sizeVars, radiusVars, durationVars, easeVars, fontWeightVars, typeScaleVars } from "../theme/tokens.stylex.js";
import { useTabListContext } from "./TabListContext.js";
import "./tab.markers.stylex.js";
import { tabScope } from "./tab.markers.stylex.js";
import { useLinkComponent } from "../Link/useLinkComponent.js";
import { mergeProps } from "../utils/index.js";
import { EDGE_COMP_ATTR } from "../Layout/edgeCompensation.stylex.js";
import { themeProps } from "../utils/themeProps.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
// Styles
// =============================================================================

const styles = {
  base: {
    kVAEAm: "x1n2onr6",
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kOIVth: "xzye2dw",
    kg3NbH: "xrrkdod",
    kWkggS: "xjbqb8w",
    kMzoRj: "xc342km",
    ksu8eU: "xng3xce",
    kaIpWk: "xh6dtrn",
    kMv6JI: "xjb2p0i",
    kGuDYH: "xcr08ib",
    kLWn49: "x1kq96og",
    k63SB2: "x1sodnla",
    kMwMTN: "xv1l7n4",
    kkrTdU: "x1ypdohk",
    kybGjl: "x1hl2dhg",
    khDVqt: "xuxw1ft",
    k1ekBW: "xt3l3uh",
    kIyJzY: "xuedmi6",
    kAMwcw: "xlr8y92",
    kI3sdo: "x17nn4n9",
    kInvED: "x1wfwxd8 x7s97pk",
    $$css: true
  },
  hoverBg: {
    kVAEAm: "x10l6tqk",
    kpwlN0: "x10a8y8t",
    kogj98: "x1bpp3o7",
    kzqmXN: "xh8yej3",
    kaIpWk: "xh6dtrn",
    kfzvcC: "x47corl",
    kWkggS: "xjbqb8w x1a1zr74",
    k1ekBW: "x15406qy",
    kIyJzY: "xuedmi6",
    kAMwcw: "xlr8y92",
    $$css: true
  },
  selected: {
    kMwMTN: "x1tgivj0",
    k63SB2: "x2mo6ok",
    $$css: true
  },
  icon: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kmuXW: "x2lah0s",
    $$css: true
  }
};
const sizeStyles = {
  sm: {
    kZKoxP: "x6k0iem",
    $$css: true
  },
  md: {
    kZKoxP: "x1ueg155",
    $$css: true
  },
  lg: {
    kZKoxP: "xssyfek",
    $$css: true
  }
};

// Hover bg uses the standard element size (one step smaller than tab)
const hoverSizeStyles = {
  sm: {
    kZKoxP: "x6k0iem",
    $$css: true
  },
  md: {
    kZKoxP: "x1ueg155",
    $$css: true
  },
  lg: {
    kZKoxP: "xssyfek",
    $$css: true
  }
};
const layoutStyles = {
  fill: {
    kUk6DE: "x98rzlu",
    kjj79g: "xl56j7k",
    $$css: true
  }
};
const iconSizeStyles = {
  sm: {
    kzqmXN: "x6jxa94",
    kZKoxP: "x1v9usgg",
    $$css: true
  },
  md: {
    kzqmXN: "x1kky2od",
    kZKoxP: "xlup9mm",
    $$css: true
  },
  lg: {
    kzqmXN: "x1xp8n7a",
    kZKoxP: "xmix8c7",
    $$css: true
  }
};

/**
 * Tab item component. Renders as an anchor when `href` is provided,
 * otherwise as a button.
 *
 * @example
 * ```
 * <TabList value={tab} onChange={setTab}>
 *   <Tab value="general" label="General" />
 *   <Tab value="advanced" label="Advanced" />
 * </TabList>
 * ```
 */
export function Tab({
  as,
  ref,
  value,
  label,
  isLabelHidden = false,
  href,
  icon,
  selectedIcon,
  endContent,
  xstyle,
  className,
  style,
  ...restProps
}) {
  const tabListCtx = useTabListContext();
  const LinkComponent = useLinkComponent(as);
  const isSelected = tabListCtx.value === value;
  const size = tabListCtx.size;
  const isFill = tabListCtx.layout === 'fill';
  const displayIcon = isSelected && selectedIcon ? selectedIcon : icon;
  const hasVisibleLabel = !isLabelHidden && label !== '';
  const handleSelect = useCallback(() => {
    tabListCtx.onChange(value);
  }, [tabListCtx, value]);
  const iconElement = displayIcon ? /*#__PURE__*/_jsx("span", {
    ...stylex.props(styles.icon, iconSizeStyles[size]),
    children: displayIcon
  }) : null;
  const sharedProps = {
    ...restProps,
    ...(isLabelHidden ? {
      'aria-label': label
    } : {}),
    [EDGE_COMP_ATTR]: '',
    'data-tab-value': value,
    'aria-current': isSelected ? 'page' : undefined,
    // Roving tabindex: the tab strip is a single Tab stop. The selected tab is
    // the tabbable one; the rest are reachable via arrow keys (handled by
    // TabList's onKeyDown). When no tab is selected, TabList's repair effect
    // makes the first stop tabbable.
    tabIndex: isSelected ? 0 : -1,
    ...mergeProps(themeProps('tab', {
      selected: isSelected ? 'selected' : null
    }), stylex.props(styles.base, sizeStyles[size], isSelected && styles.selected, isFill && layoutStyles.fill, tabScope, xstyle), className, style)
  };
  const hoverBgElement = /*#__PURE__*/_jsx("span", {
    "aria-hidden": "true",
    ...stylex.props(styles.hoverBg, hoverSizeStyles[size])
  });
  const indicatorElement = /*#__PURE__*/_jsx("span", {
    ...mergeProps(themeProps('tab-indicator', {
      selected: isSelected ? 'selected' : null
    }), {
      0: {
        className: "x10l6tqk xqmqy1e xnp31yv xmz3bnw x36qwtl xjspbzw x47corl xnpjden xuedmi6 xlr8y92 xjbqb8w xg01cxk"
      },
      1: {
        className: "x10l6tqk xqmqy1e xnp31yv xmz3bnw x36qwtl xjspbzw x47corl xnpjden xuedmi6 xlr8y92 x1ewilqj x1hc1fzr"
      }
    }[!!isSelected << 0])
  });
  const labelElement = hasVisibleLabel ? /*#__PURE__*/_jsxs("span", {
    ...{
      className: "xwz0xwf"
    },
    children: [/*#__PURE__*/_jsx("span", {
      ...{
        className: "x1agbcgv xcrlgei"
      },
      children: label
    }), /*#__PURE__*/_jsx("span", {
      "aria-hidden": "true",
      ...{
        className: "x1agbcgv xcrlgei xlshs6z x47corl x2mo6ok"
      },
      children: label
    })]
  }) : null;
  const endContentElement = endContent ? /*#__PURE__*/_jsx("span", {
    ...{
      className: "x3nfvp2 x6s0dn4 x2lah0s"
    },
    children: endContent
  }) : null;
  if (href != null) {
    return /*#__PURE__*/_jsxs(LinkComponent, {
      ref: ref,
      href: href,
      onClick: handleSelect,
      ...sharedProps,
      children: [hoverBgElement, iconElement, labelElement, endContentElement, indicatorElement]
    });
  }
  return /*#__PURE__*/_jsxs("button", {
    ref: ref,
    type: "button",
    onClick: handleSelect,
    ...sharedProps,
    children: [hoverBgElement, iconElement, labelElement, endContentElement, indicatorElement]
  });
}
Tab.displayName = 'Tab';